import { useEffect, useRef, useState } from 'react';
import { hubClient } from '../../services/hubClient';
import { isBotSystem } from '../../lib/botNav';
import { latestGate } from '../../lib/answerReviewView';
import { applyLineFixes, checkPieces, CYRILLIC_NOTE, fixLine, latinLines, MAX_CHECK_LINES, MAX_PIECE } from '../../lib/cyrillicCheck';

interface Fix {
  index: number;
  from: string;
  to: string;
}

/**
 * Under a text customers are matched against (text conversion P4, spec §4.6): the Latin lines, the Cyrillic the
 * converter gives them (this bot's corrections and keep-Latin words), and one click to use it. Never blocks a save;
 * a failed check shows nothing.
 */
export function CyrillicNote({ system, botId, text, onFix }: { system: string; botId: string; text: string; onFix: (next: string) => void }) {
  // the fixes and the text they were checked against: shown only while the text is still that text
  const [checked, setChecked] = useState<{ text: string; fixes: Fix[] }>({ text: '', fixes: [] });
  const gate = useRef(latestGate());

  useEffect(() => {
    const id = gate.current.begin();
    const sys = isBotSystem(system) ? system : null;
    const lines = sys && botId ? latinLines(text) : [];
    if (!sys || lines.length === 0) {
      setChecked({ text, fixes: [] });
      return;
    }
    const timer = setTimeout(() => {
      // a long line goes in pieces the convert route accepts; at most 2 × MAX_CHECK_LINES requests per pass
      const pieces = lines.flatMap((l, i) => checkPieces(l.line, MAX_PIECE).map((piece) => ({ i, piece }))).slice(0, 2 * MAX_CHECK_LINES);
      void Promise.allSettled(pieces.map((p) => hubClient.textConvert({ system: sys, botId }, p.piece))).then((results) => {
        if (!gate.current.isCurrent(id)) return;
        const changes = lines.map(() => [] as { raw: string; out: string; kind: string }[]);
        results.forEach((r, k) => {
          if (r.status === 'fulfilled') changes[pieces[k].i].push(...(r.value.changes ?? []));
        });
        const found: Fix[] = [];
        lines.forEach((l, i) => {
          const to = fixLine(l.line, changes[i]);
          if (to !== l.line) found.push({ index: l.index, from: l.line, to });
        });
        setChecked({ text, fixes: found });
      });
    }, 600);
    return () => clearTimeout(timer);
  }, [system, botId, text]);

  const fixes = checked.text === text ? checked.fixes : [];
  if (fixes.length === 0) return null;
  return (
    <div className="text-sm rounded border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800 space-y-1">
      <p>{CYRILLIC_NOTE}</p>
      <ul className="space-y-1">
        {fixes.map((f) => (
          <li key={f.index} className="flex flex-wrap items-center gap-2">
            <span className="font-mono">{f.from}</span>
            <span aria-hidden>→</span>
            <span className="font-mono">{f.to}</span>
            <button type="button" className="px-2 py-0.5 rounded border border-amber-400 bg-white" onClick={() => onFix(applyLineFixes(text, [f]))}>
              Use Cyrillic
            </button>
          </li>
        ))}
      </ul>
      {fixes.length > 1 && (
        <button type="button" className="px-2 py-0.5 rounded border border-amber-400 bg-white" onClick={() => onFix(applyLineFixes(text, fixes))}>
          Fix all
        </button>
      )}
    </div>
  );
}
